import { useEffect, useState } from 'react';
import { BookOpen, Download, Users, Phone, Mail, MapPin } from 'lucide-react';
import { api } from '../../lib/api';
import type { StaffInfo, StaffHandbook } from '../../types';
import { Spinner, EmptyState, Avatar } from '../../components/ui';

export default function StaffBook() {
  const [staff, setStaff] = useState<StaffInfo[]>([]);
  const [handbook, setHandbook] = useState<StaffHandbook | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get<StaffInfo[]>('/auth/users?role=staff').catch(() => [] as StaffInfo[]),
      api.get<StaffHandbook | null>('/handbooks/latest').catch(() => null),
    ])
      .then(([staffList, latest]) => {
        setStaff(staffList);
        setHandbook(latest);
      })
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <Spinner label="Loading staff book..." />;

  return (
    <div className="space-y-8">
      <h1 className="text-2xl font-bold">Staff Book</h1>

      <section>
        <div className="flex items-center gap-2 mb-4">
          <Users className="h-5 w-5 text-primary" />
          <h2 className="text-lg font-semibold">Staff Directory</h2>
        </div>
        {staff.length === 0 ? (
          <EmptyState message="No staff members found." />
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {staff.map((st) => (
              <div key={st.id} className="bg-white p-6 rounded-lg shadow-lg">
                <div className="flex items-center space-x-4">
                  <Avatar src={st.image_url} name={st.name} className="w-16 h-16" />
                  <div>
                    <h3 className="font-semibold">{st.name}</h3>
                    <p className="text-sm text-gray-500 capitalize">{st.role_title || st.role}</p>
                  </div>
                </div>
                <div className="mt-4 space-y-1 text-sm text-gray-500">
                  <p className="flex items-center"><Mail className="h-4 w-4 mr-2 text-gray-400" /> {st.email}</p>
                  {st.phone && <p className="flex items-center"><Phone className="h-4 w-4 mr-2 text-gray-400" /> {st.phone}</p>}
                  {st.estate && <p className="flex items-center"><MapPin className="h-4 w-4 mr-2 text-gray-400" /> {st.estate}</p>}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      <section>
        <div className="flex items-center gap-2 mb-4">
          <BookOpen className="h-5 w-5 text-primary" />
          <h2 className="text-lg font-semibold">Staff Handbook</h2>
        </div>
        {handbook ? (
          <div className="bg-white p-6 rounded-lg shadow-lg flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center">
              <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mr-4">
                <BookOpen className="h-6 w-6 text-primary" />
              </div>
              <div>
                <p className="font-semibold">{handbook.title}</p>
                <p className="text-sm text-gray-500">{handbook.filename}</p>
              </div>
            </div>
            <a
              href={`/api/handbooks/${handbook.id}/download`}
              className="inline-flex items-center px-4 py-2 bg-primary text-black rounded-lg hover:bg-primary-dark text-sm"
            >
              <Download className="h-4 w-4 mr-2" /> Download
            </a>
          </div>
        ) : (
          <EmptyState message="No staff handbook has been uploaded yet." />
        )}
      </section>
    </div>
  );
}
